import {
  Box,
  Button,
  Flex,
  Spacer,
  Text,
  Popover,
  VStack,
  StackSeparator,
  Checkbox,
  useDisclosure,
  Link,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import i18next from "i18next";
// import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Access,
  AccessRole,
  AccessUpdateRequest,
} from "../../../access/models/Access";
import CustomAvatar from "../CustomAvatar";
import { CustomPopover } from "../CustomPopover";
import { ChevronDownIcon } from "../icons";

interface Props {
  access: Access;
  isInherited?: Boolean;
  updateAccess: (accessId: string, data: AccessUpdateRequest) => any;
  removeAccess: (accessId: string) => any;
}
const AccessItemBase: React.FC<Props> = ({
  access,
  isInherited = false,
  updateAccess,
  removeAccess,
}) => {
  const popoverState = useDisclosure();
  const [roles, setRoles] = useState<Set<AccessRole>>(new Set());

  useEffect(() => {
    setRoles(new Set(access.roles));
  }, [access]);

  const onChangeRole = (role: AccessRole, checked: boolean) => {
    setRoles((prev) => {
      if (checked) {
        if (role === AccessRole.Manager) {
          prev.clear();
        }
        prev.add(role as AccessRole);
      } else {
        prev.delete(role as AccessRole);
      }

      return new Set(prev);
    });
  };

  const onClose = () => {
    var notDirty =
      access.roles.length === roles.size &&
      access.roles.every((role) => roles.has(role));
    if (!notDirty) {
      updateAccess(access.id, {
        roles: [...roles],
      });
    }
    popoverState.onClose();
  };

  const onRemove = () => {
    removeAccess(access.id);
    popoverState.onClose();
  };

  return (
    <Flex
      mx={-10}
      px={10}
      alignItems="center"
      minHeight={10}
      _hover={
        isInherited
          ? undefined
          : {
              background: "#edf2f7",
            }
      }
    >
      <CustomAvatar size="xs" name={access.email} />
      <Text ml={2}>{access.email}</Text>
      <Spacer />

      {isInherited ? (
        <Text>{formatRoles(access.roles)}</Text>
      ) : (
        <CustomPopover
          trigger={
            <Button disabled={access.me} variant="ghost">
              {formatRoles(access.roles)} <ChevronDownIcon />
            </Button>
          }
          open={popoverState.open}
          onOpen={popoverState.onOpen}
          onClose={onClose}
        >
          <Popover.Body>
            <VStack alignItems="start" separator={<StackSeparator />}>
              {Object.values(AccessRole).map((role, i) => (
                <Checkbox.Root
                  checked={roles.has(role) || roles.has(AccessRole.Manager)}
                  disabled={
                    roles.has(AccessRole.Manager) && role !== AccessRole.Manager
                  }
                  name={role}
                  onCheckedChange={({ checked }) =>
                    onChangeRole(role, !!checked)
                  }
                  colorPalette="primary"
                  display="flex"
                  pl={2}
                  key={i}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>
                    <Box ml={1} flexGrow={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {i18next.t(`shared:shareModal.roles.${role}`)}
                      </Text>
                      <Text fontSize="xs">
                        {i18next.t(
                          `shared:shareModal.roleDescriptions.${role}`
                        )}
                      </Text>
                    </Box>
                  </Checkbox.Label>
                </Checkbox.Root>
              ))}
              <Link fontSize="sm" fontWeight="semibold" onClick={onRemove}>
                {i18next.t(`shared:shareModal.revokeButton`)}
              </Link>
            </VStack>
          </Popover.Body>
        </CustomPopover>
      )}
    </Flex>
  );
};

export const AccessItem = observer(AccessItemBase);

const formatRoles = (roles: AccessRole[]) =>
  roles
    .map((role) => i18next.t(`shared:shareModal.roles.${role}`))
    .join(", ") || i18next.t(`shared:shareModal.rolePlaceholder`);
